import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { BarChart3, TrendingUp, ShieldCheck, DollarSign } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://api.jaychrisedi.com';
const NAVY = '#0D2B5E';
const BLUE = '#1A6BB5';
const GREEN = '#6DC234';
const GREEN_DIM = '#5AAD28';

const Analytics = ({ setPage }) => {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, pRes] = await Promise.all([
          fetch(API+'/api/transactions/summary',{headers:{Authorization:'Bearer '+token}}),
          fetch(API+'/api/partners',{headers:{Authorization:'Bearer '+token}})
        ]);
        setSummary(await sRes.json());
        setPartners(await pRes.json());
      } catch(e) {}
    };
    load();
  }, []);

  const complianceScore = summary?.total > 0
    ? Math.round(((summary?.completed||0) / summary.total) * 100)
    : 100;

  const panel = {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #E2E8F0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
  };

  const statCards = [
    { icon: <ShieldCheck size={26}/>, value: complianceScore+'%', label:'Compliance Score',  bg:`linear-gradient(135deg,${GREEN_DIM},${GREEN})` },
    { icon: <BarChart3    size={26}/>, value: summary?.total||0,   label:'Total Transactions', bg:`linear-gradient(135deg,${BLUE},#0D4E8A)` },
    { icon: <DollarSign   size={26}/>, value: '$'+(summary?.totalRevenue||0).toLocaleString(), label:'Revenue Processed', bg:`linear-gradient(135deg,#071830,${NAVY})` },
    { icon: <TrendingUp   size={26}/>, value: partners?.length||0, label:'Active Partners',   bg:`linear-gradient(135deg,#2E8DD4,${BLUE})` },
  ];

  return (
    <div style={{ minHeight:'100vh', background:'#F4F6F9', padding:'28px 24px' }}>
      <div style={{ maxWidth:'1200px', margin:'0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom:'32px' }}>
          <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:'11px', fontWeight:'700', color:BLUE, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:'4px' }}>Overview</div>
          <h1 style={{ fontSize:'28px', fontWeight:'900', color:NAVY, margin:0, letterSpacing:'-0.03em' }}>Analytics & Compliance</h1>
          <p style={{ color:'#64748B', fontSize:'14px', margin:'4px 0 0' }}>Real-time insights into your EDI operations</p>
        </div>

        {/* Stat cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'18px', marginBottom:'24px' }}>
          {statCards.map((card, i) => (
            <div key={i} style={{ background:card.bg, borderRadius:'16px', padding:'24px', boxShadow:'0 4px 16px rgba(0,0,0,0.12)' }}>
              <div style={{ color:'rgba(255,255,255,0.85)', marginBottom:'10px' }}>{card.icon}</div>
              <div style={{ fontSize:'40px', fontWeight:'900', color:'white', fontFamily:"'JetBrains Mono',monospace", lineHeight:1 }}>{card.value}</div>
              <div style={{ color:'rgba(255,255,255,0.75)', fontSize:'13px', marginTop:'6px' }}>{card.label}</div>
            </div>
          ))}
        </div>

        {/* Middle row */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px' }}>
          {/* Compliance breakdown */}
          <div style={panel}>
            <h3 style={{ color:NAVY, fontSize:'17px', fontWeight:'700', marginBottom:'20px' }}>Compliance Breakdown</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
              {[
                { label:'Completed', value:summary?.completed||0, color:GREEN,    pct: summary?.total ? ((summary?.completed||0)/summary.total)*100 : 100 },
                { label:'Pending',   value:summary?.pending||0,   color:'#F59E0B', pct: summary?.total ? ((summary?.pending||0)/summary.total)*100 : 0 },
                { label:'Errors',    value:summary?.errors||0,    color:'#EF4444', pct: summary?.total ? ((summary?.errors||0)/summary.total)*100 : 0 },
              ].map(row => (
                <div key={row.label}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}>
                    <span style={{ color:'#64748B', fontSize:'13px' }}>{row.label}</span>
                    <span style={{ color:row.color, fontWeight:'700', fontSize:'13px' }}>{row.value}</span>
                  </div>
                  <div style={{ height:'8px', borderRadius:'4px', background:'#F1F5F9' }}>
                    <div style={{ height:'100%', borderRadius:'4px', background:row.color, width:`${row.pct}%`, transition:'width 0.6s ease' }}/>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Partner performance */}
          <div style={panel}>
            <h3 style={{ color:NAVY, fontSize:'17px', fontWeight:'700', marginBottom:'20px' }}>Partner Performance</h3>
            {partners?.length > 0 ? partners.map(p => (
              <div key={p.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 0', borderBottom:'1px solid #F1F5F9' }}>
                <div style={{ width:'36px', height:'36px', borderRadius:'10px', background:`linear-gradient(135deg,${GREEN_DIM},${GREEN})`, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:'900', fontSize:'16px', flexShrink:0 }}>
                  {p.name?.charAt(0)}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ color:NAVY, fontWeight:'600', fontSize:'14px' }}>{p.name}</div>
                  <div style={{ color:'#94A3B8', fontSize:'11px', fontFamily:'monospace' }}>{p.ediIdentifier}</div>
                </div>
                <div style={{ padding:'4px 10px', borderRadius:'6px', fontSize:'11px', fontWeight:'700', background:p.status==='active'?'rgba(109,194,52,0.12)':'rgba(239,68,68,0.1)', color:p.status==='active'?GREEN_DIM:'#DC2626' }}>
                  {p.status}
                </div>
              </div>
            )) : <p style={{ color:'#94A3B8', fontSize:'13px' }}>No partners configured yet.</p>}
          </div>
        </div>

        {/* Document type summary */}
        <div style={panel}>
          <h3 style={{ color:NAVY, fontSize:'17px', fontWeight:'700', marginBottom:'20px' }}>Document Type Summary</h3>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:'14px' }}>
            {[
              { type:'850', name:'Purchase Orders',    color:BLUE,      icon:'📥' },
              { type:'855', name:'PO Acknowledgments', color:GREEN_DIM, icon:'✅' },
              { type:'810', name:'Invoices',           color:NAVY,      icon:'💰' },
              { type:'856', name:'Ship Notices (ASN)', color:'#D97706', icon:'🚚' },
              { type:'997', name:'Acknowledgments',    color:'#2E8DD4', icon:'📋' },
            ].map(doc => (
              <div key={doc.type} style={{ background:'#F8FAFD', borderRadius:'12px', padding:'16px', textAlign:'center', border:'1px solid #E2E8F0' }}>
                <div style={{ fontSize:'26px', marginBottom:'8px' }}>{doc.icon}</div>
                <div style={{ fontSize:'26px', fontWeight:'900', color:doc.color, fontFamily:"'JetBrains Mono',monospace" }}>{summary?.byType?.[doc.type]||0}</div>
                <div style={{ color:'#64748B', fontSize:'12px', marginTop:'4px' }}>{doc.name}</div>
                <div style={{ color:'#94A3B8', fontSize:'11px', fontFamily:'monospace' }}>EDI {doc.type}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Analytics;
